import React, { useState } from 'react';
import { useRouter } from 'next/router';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { useQuery } from '@apollo/client';
import { Trans } from '@lingui/react';
import Label from '@components/form/Label';
import Select from '@components/form/Select';
import Grid from '@components/CSSGrid';
import {
  Box,
  Flex,
  ShowInLargeUp,
  ShowInMediumDown,
} from '@components/layout/Grid';
import LoadMoreSection from '@components/LoadMoreSection';
import EmptyResultsGenre from '@ecosystems/EmptyResultsGenre';
import { videoSettingsModalOpenVar, GET_VIDEO_SETTINGS } from '@lib/apollo';
import VideoCard from '@organisms/cards/VideoCard';
import VideosGenresVideosSkeleton from '@skeletons/VideosGenresVideosSkeleton';
import AnalyticsManager from '@lib/analytics/manager';
import { sectionTitleStyle } from '@components/texts';
import { useVideosQuery } from '@gql/generated';
import { WellnessOfferCard } from '@organisms/cards/WellnessOfferCard';
import { YearlyOfferCard } from '@organisms/cards/YearlyOfferCard';
import { SpringBoostCard } from '@organisms/cards/SpringBoostCard';

const VideoGenresList = ({
  query,
  genre,
  taxonomies,
  selectedTerms,
  clearAll,
  renderTeachersSection,
}) => {
  const router = useRouter();
  const [orderBy, setOrderBy] = useState('published_descending');
  const [fetchingMore, setFetchingMore] = useState(false);
  const { data: preferences, loading: loadingPreferences } = useQuery(
    GET_VIDEO_SETTINGS
  );

  const variables = {
    taxonomies: Object.keys(selectedTerms).map(key => ({
      taxonomy: key,
      values: selectedTerms[key],
    })),
    perPage: 12,
    query: query,
    genre: genre === 'all' ? '' : genre,
    useLocale: false,
    orderBy: orderBy,
    ...Object.assign({}, preferences?.videoSettings),
  };

  const page = parseInt(router.query?.page as string) || 1;

  const getVideosResponse = useVideosQuery({
    variables: Object.assign(variables, preferences?.videoSettings, { page }),
  });

  useDeepCompareEffect(() => {
    if (preferences?.videoSettings) {
      AnalyticsManager().sendSearchEvent({
        taxonomies: variables.taxonomies,
        query: variables.query,
        genre: variables.genre,
      });
    }
  }, [preferences, variables]);

  const openSettingModal = () => {
    videoSettingsModalOpenVar(true);
  };

  if (loadingPreferences) return <VideosGenresVideosSkeleton />;

  const { data, loading } = getVideosResponse;
  if (loading && !data) return <VideosGenresVideosSkeleton />;

  const videos = data?.videos;

  if (videos?.totalCount === 0) {
    return (
      <EmptyResultsGenre
        keyword={query}
        emptyTerms={!selectedTerms.length}
        clearAll={clearAll}
        //TODO: FIX THIS
        openSettingModal={openSettingModal}
      />
    );
  }

  let selectedTagsText;
  if (Object.keys(selectedTerms || {}).length > 0) {
    const flattenTags = taxonomies
      .reduce((acc, taxonomy) => {
        if (
          selectedTerms.hasOwnProperty(taxonomy.slug) &&
          // we exclude duration from the list
          taxonomy.slug !== 'durations'
        ) {
          acc.push(
            taxonomy.terms.reduce((acc, term) => {
              if (selectedTerms[taxonomy.slug].includes(term.slug)) {
                acc.push(term.name);
              }
              return acc;
            }, [])
          );
        }

        return acc;
      }, [])
      .flat();

    let selectedTags = [];
    if (flattenTags.length > 4) {
      selectedTags = flattenTags.slice(0, 4);
    } else {
      selectedTags = [...flattenTags];
    }

    selectedTagsText = selectedTags.join(', ');
    if (flattenTags.length > 4) {
      selectedTagsText = (
        <>
          {selectedTagsText}{' '}
          <Trans
            id="metadata.more_items"
            values={{ number: flattenTags.length - 4 }}
          />
        </>
      );
    }
  }

  return (
    <React.Fragment>
      <Flex justifyContent="space-between" alignItems="center" mb="20px">
        <Box>
          <Label css={sectionTitleStyle}>
            <Trans
              id="videos_genres.count"
              message={'{count} hits'}
              values={{ count: videos?.totalCount }}
            />
            {query && (
              <React.Fragment>
                {' '}
                <Trans
                  id="videos_genres.keyword"
                  message={'for keyword "{keyword}"'}
                  values={{ keyword: query }}
                />
              </React.Fragment>
            )}
            {selectedTagsText ? (
              <>
                {query ? ',' : ' for'} {selectedTagsText}
              </>
            ) : null}
          </Label>
        </Box>
        <Box>
          <Box as={ShowInLargeUp}>
            <Flex mx="-10px" alignItems="center">
              <Box px="10px">
                <Select
                  onChange={event => {
                    setOrderBy(event?.target?.value as string);
                  }}
                  selectedValue={orderBy}
                  options={[
                    {
                      value: 'published_descending',
                      name: (
                        <Trans
                          id="sortby.published_at"
                          message={`Published At`}
                        />
                      ),
                    },
                    {
                      value: 'duration_ascending',
                      name: <Trans id="sortby.time" message={`Time`} />,
                    },
                  ]}
                />
              </Box>
            </Flex>
          </Box>
        </Box>
      </Flex>
      <Flex as={ShowInMediumDown} alignItems="end" mb={15}>
        <Box>
          <Select
            onChange={event => {
              setOrderBy(event?.target?.value as string);
            }}
            selectedValue={orderBy}
            options={[
              {
                value: 'published_descending',
                name: <Trans id="sortby.published_at" message="Published At" />,
              },
              {
                value: 'duration_ascending',
                name: <Trans id="sortby.time" message="Time" />,
              },
            ]}
          />
        </Box>
      </Flex>
      <Grid cardWidth={300} data-testid="VideoGenresList--VideosContainer">
        <YearlyOfferCard />
        <SpringBoostCard />
        {videos?.data?.map?.(video => {
          return (
            <VideoCard
              key={video.id}
              className="VideoGenresList--VideoCard"
              video={{
                id: video.id,
                identifier: video.identifier,
                kind: video.kind,
                title: video.content.title,
                cursorAt: video.cursorAt,
                description: video.content?.shortDescription,
                thumbnailUrl: video.assets?.thumbnailUrl,
                flavor: video?.taxonomies?.flavor
                  ? video.taxonomies.flavor.name
                  : '',
                publishedAt: video.meta.publishedAt,
                duration: video.meta.minutes,
                genreName: video.taxonomies?.genre?.name
                  ? video.taxonomies.genre.name
                  : '',
                genreSlug: video.taxonomies?.genre?.slug
                  ? video.taxonomies.genre.slug
                  : '',
                instructors: video.taxonomies?.instructors || [],
                primaryStyle: video.primaryStyle,
              }}
              data-testid="VideoGenresList--VideoCard"
            />
          );
        })}
      </Grid>
      <LoadMoreSection
        data={videos}
        loading={fetchingMore}
        onLoadMoreClick={async () => {
          setFetchingMore(true);
          await getVideosResponse.fetchMore({
            variables: Object.assign(variables, preferences?.videoSettings, {
              page: videos.page + 1,
            }),
          });
          router.push(
            {
              pathname: router.pathname,
              query: { ...router.query, page: videos.page + 1 },
            },
            null,
            {
              shallow: true,
              scroll: false,
            }
          );
          setFetchingMore(false);
        }}
        data-testid="VideoGenresList--LoadMore"
      />
      {renderTeachersSection()}
    </React.Fragment>
  );
};

export default VideoGenresList;
